import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApprovalService } from "../services/approval.service";
import { ApprovalForm } from "./ApprovalForm";
import { GenericZoomFile } from "../../../Shared/components/GenericZoomFile";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { AuthService } from "../../Auth/services/AuthService";
import { RejectApprovalForm } from "./RejectApprovalForm";
import { useTranslation } from "react-i18next";
import { PermitHeader } from "../../../Shared/components/PermitHeader";
import { Permit } from "./Permit";

export const Approval = () => {
  const navigate = useNavigate();
  const {t} = useTranslation()
  const message = t('loading_message')

  const [searchParams] = useSearchParams();
  const approval = searchParams.get("approval");
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const findApproval = async () => {
    try {
      setIsLoading(true);
      return await ApprovalService.getById(approval ?? "");
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["approval"],
    queryFn: findApproval,
    placeholderData: (previous) => previous?.data,
  });

  const onApproval = async () => {
    onAddAction();
  };

  const onRejectRequest = async () => {
    onRejectAction();
  };

  let isAllowed = AuthService.isManagementAllowed();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };
  const onAddAction = () => setIsModalOpen(!isModalOpen);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const toggleRejectModal = () => setIsRejectModalOpen(!isRejectModalOpen);
  const onRejectAction = () => setIsRejectModalOpen(!isRejectModalOpen);

  const onViewCompany = () => {
    navigate(
      `/services/accounts/companies/details/?company=${query.data?.company?.id}`
    );
  };

  return (
    <>
      <div className="">
        {isModalOpen && (
          <ApprovalForm
            onClose={toggleModal}
            approval={query.data!}
            company={query.data?.company}
          />
        )}

        {isRejectModalOpen && (
          <RejectApprovalForm
            onClose={toggleRejectModal}
            approval={query.data!}
          />
        )}
        <div className="antialiased dark:bg-gray-900">
          <div className="mx-auto max-w-screen-lg px-4 2xl:px-0">
              {query?.data?.isAuthorized ?<Permit data={query?.data} />:<>
              <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:gap-4 lg:gap-4">
                <div>
                  <p className="text-gray-600">{t('agreed_number_of_trips')}</p>
                  <p className="text-gray-800 font-bold">
                    {query.data?.agreedTripNumber ?? t('pending')}
                  </p>
                </div>{" "}
                <div>
                  <p className="text-gray-600">{t('authorization_status')}</p>
                  <span
                    className={`inline-block px-3 py-1 rounded-md text-white text-sm ${
                      query.data?.isAuthorized ? "bg-blue-500" : "bg-red-500"
                    }`}
                  >
                    {query.data?.isAuthorized ? t('authorized') : t('not_authorized')}
                  </span>
                  {!query.data?.isAuthorized && query.data?.reason && (
                    <p className="text-gray-600 mt-2">
                      {t('reason')}:{" "}
                      <span className="text-gray-800 font-bold">
                        {query.data?.reason}
                      </span>
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-gray-600">{t('date_requested')}</p>
                  <p className="text-gray-800 font-bold">
                    {UtilService.toLocalDate(query.data?.requestedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{t('date_approved')}</p>
                  <p className="text-gray-800 font-bold">
                    {UtilService.toLocalDate(query.data?.authorizedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{t('authorized_route')}</p>
                  <p className="text-gray-800 font-bold">
                    {query.data?.authorizedRoute}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">{t('authorized_goods')}</p>
                  <p className="text-gray-800 font-bold">
                    {query.data?.authorizedGoods}
                  </p>
                </div>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-xl font-semibold text-gray-800">
                  {t('company_details')}
                </h3>
                <div className="flex items-start mt-4 gap-4">
                  {query.data?.company?.profile?.picture?.[0]?.file ? (
                    <GenericZoomFile
                      src={query.data?.company?.profile?.picture?.[0]}
                      height={100}
                      width={100}
                    />
                  ) : (
                    ""
                  )}
                  <div className="cursor-pointer" onClick={onViewCompany}>
                    <p className="text-lg font-bold">
                      {query.data?.company?.name}
                    </p>
                    <p className="text-gray-600 mt-1">
                      {query.data?.company?.address}
                    </p>
                  </div>
                </div>
              </div>

              <div className="py-10 flex row">
                {isAllowed &&
                  !query.data?.isAuthorized &&
                  query?.data?.requestedAt != null && (
                    <GenericButton
                      isFull={false}
                      type="button"
                      className="py-2"
                      onClick={() => onApproval()}
                    >
                      {t('approve')}
                    </GenericButton>
                  )}
                <div className="">&nbsp;&nbsp;</div>
                {isAllowed && query?.data?.requestedAt != null && (
                  <GenericButton
                    isFull={false}
                    type="button"
                    className="bg-gray-500 py-2"
                    onClick={onRejectRequest}
                  >
                    {t('reject_approval')}
                  </GenericButton>
                )}
              </div></>}
          </div>
        </div>
        {showToast && (
          <Toast
            type={toast?.type}
            message={toast?.message ?? ""}
            onClose={() => setShowToast(false)}
          />
        )}
        {isLoading && <OverlayLoader message={message} />}
      </div>
    </>
  );
};
